import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import RiskIndexInfoDrawerView from '@app/src/components/Drawer/Views/RiskIndexInfoDrawerView'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import useIsTextTruncated from '@app/src/hooks/useIsTextTruncated'
import type { OrganizationRiskSetting } from '@app/src/types/resourceExplorer'
import { RiskIndexType } from '@app/src/types/resourceExplorer'
import { Factory, InfoOutlined } from '@mui/icons-material'
import { Box, Checkbox, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import RiskIndexSourceIcon from './RiskIndexSourceIcon'

interface RiskIndexRowProps {
  setting: OrganizationRiskSetting
  isEditMode: boolean
  isTopicActive: boolean
  onToggle: (isActivated: boolean) => void
}

const RiskIndexRow: React.FC<RiskIndexRowProps> = ({ setting, isEditMode, isTopicActive, onToggle }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()

  const { riskType } = setting
  const isIndustryRisk = riskType.riskIndexType === RiskIndexType.Industry
  const labelId = isIndustryRisk ? 'general.industryRisk' : 'general.countryRisk'
  const label = formatMessage({ id: labelId })
  const source = isIndustryRisk ? '' : formatMessage({ id: `schemas.risk.riskIndexes.${riskType.source}.source` })
  const checkboxLabel = source ? `${label} – ${source}` : label
  const { ref: sourceRef, isTruncated: isSourceTruncated } = useIsTextTruncated<HTMLSpanElement>(
    source,
    !isIndustryRisk,
  )

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        p: 1,
        minHeight: 56,
        borderRadius: '8px',
        backgroundColor: 'grey.50',
      }}
    >
      <Avatar
        variant="rounded"
        size={AvatarSize.Large}
        sx={({ palette }) => ({
          backgroundColor: isIndustryRisk ? 'grey.100' : 'white',
          border: isIndustryRisk ? 'none' : `1px solid ${palette.grey[300]}`,
        })}
      >
        {isIndustryRisk ? (
          <Factory sx={{ color: 'grey.600', fontSize: 28 }} />
        ) : (
          <RiskIndexSourceIcon source={riskType.source} />
        )}
      </Avatar>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" noWrap>
            {label}
          </Typography>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            aria-label={formatMessage({ id: 'general.showMore' })}
            onClick={() => openDrawer(<RiskIndexInfoDrawerView riskType={riskType} />)}
          >
            <InfoOutlined fontSize="small" sx={{ color: 'grey.600' }} />
          </IconButton>
        </Stack>
        {!isIndustryRisk && (
          <Tooltip title={isSourceTruncated ? source : ''}>
            <Typography
              ref={sourceRef}
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block', lineHeight: 1.25, mt: -0.25 }}
            >
              {source}
            </Typography>
          </Tooltip>
        )}
      </Box>

      {isTopicActive && (
        <Checkbox
          checked={setting.isActivated}
          disabled={!isEditMode}
          onChange={(_, checked) => onToggle(checked)}
          inputProps={{ 'aria-label': checkboxLabel }}
        />
      )}
    </Stack>
  )
}

export default RiskIndexRow
